import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RedButton } from "@/components/Layout";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AdminSchedule() {
  const [sched, setSched] = useState(null);
  const [closures, setClosures] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [newClosure, setNewClosure] = useState({ start_date: "", end_date: "", reason: "" });
  const [newOverride, setNewOverride] = useState({ date: "", open: true, start: "08:00", end: "20:00", reason: "" });

  const load = async () => {
    try {
      const [s, c, o] = await Promise.all([
        api.get("/admin/schedule"), api.get("/admin/closures"), api.get("/admin/overrides"),
      ]);
      setSched(s.data); setClosures(c.data); setOverrides(o.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!sched) return <p className="text-zinc-500">Chargement...</p>;

  const setDay = (i, patch) => setSched({ ...sched, week: { ...sched.week, [i]: { ...sched.week[i], ...patch } } });

  const saveSchedule = async () => {
    try { await api.put("/admin/schedule", sched); toast.success("Horaires enregistrés"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addBreak = (i) => setDay(i, { breaks: [...(sched.week[i].breaks || []), { start: "12:00", end: "14:00" }] });
  const setBreak = (i, bi, patch) => setDay(i, {
    breaks: sched.week[i].breaks.map((b, k) => (k === bi ? { ...b, ...patch } : b)),
  });
  const delBreak = (i, bi) => setDay(i, { breaks: sched.week[i].breaks.filter((_, k) => k !== bi) });

  return (
    <div className="grid gap-10" data-testid="admin-schedule">
      <div className="border border-zinc-800 bg-[#0a0a0a] p-5 md:p-7">
        <div className="overline mb-5">Horaires d'ouverture par jour</div>
        <div className="grid gap-4">
          {DAYS.map((label, i) => {
            const d = sched.week[String(i)] || {};
            return (
              <div key={i} className="border border-zinc-900 p-4" data-testid={`schedule-day-${i}`}>
                <div className="grid gap-4 sm:grid-cols-[150px,1fr,1fr,auto] items-end">
                  <label className="flex items-center gap-3 text-white font-head text-lg">
                    <input type="checkbox" checked={!!d.open} data-testid={`schedule-open-${i}`}
                           onChange={(e) => setDay(String(i), { open: e.target.checked })} />
                    {label}
                  </label>
                  <label className="block">
                    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Début</span>
                    <input type="time" value={d.start || "08:00"} data-testid={`schedule-start-${i}`}
                           onChange={(e) => setDay(String(i), { start: e.target.value })}
                           className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2 text-white focus:border-sared focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Fin</span>
                    <input type="time" value={d.end || "20:00"} data-testid={`schedule-end-${i}`}
                           onChange={(e) => setDay(String(i), { end: e.target.value })}
                           className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2 text-white focus:border-sared focus:outline-none" />
                  </label>
                  <button onClick={() => addBreak(String(i))} data-testid={`schedule-add-break-${i}`}
                          className="border border-zinc-800 px-3 py-2 text-zinc-300 hover:border-sared text-xs font-mono-tech tracking-[0.14em]">
                    <Plus size={12} className="inline" /> PAUSE
                  </button>
                </div>
                {(d.breaks || []).map((b, bi) => (
                  <div key={bi} className="mt-3 flex items-center gap-3" data-testid={`schedule-break-${i}-${bi}`}>
                    <span className="font-mono-tech text-[10px] tracking-[0.16em] text-zinc-500">PAUSE</span>
                    <input type="time" value={b.start} onChange={(e) => setBreak(String(i), bi, { start: e.target.value })}
                           className="bg-black border border-zinc-800 px-2 py-1.5 text-white text-sm" />
                    <span className="text-zinc-600">→</span>
                    <input type="time" value={b.end} onChange={(e) => setBreak(String(i), bi, { end: e.target.value })}
                           className="bg-black border border-zinc-800 px-2 py-1.5 text-white text-sm" />
                    <button onClick={() => delBreak(String(i), bi)} className="text-zinc-500 hover:text-sared"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div className="mt-7 grid gap-5 sm:grid-cols-[200px,1fr] items-end">
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Durée d'un créneau</span>
            <select value={sched.slot_minutes} data-testid="schedule-slot-minutes"
                    onChange={(e) => setSched({ ...sched, slot_minutes: Number(e.target.value) })}
                    className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none">
              <option value={60}>1 heure</option>
              <option value={90}>1h30</option>
              <option value={120}>2 heures</option>
              <option value={180}>3 heures</option>
            </select>
          </label>
          <label className="flex items-center gap-3 text-sm text-zinc-300">
            <input type="checkbox" checked={sched.require_validation} data-testid="schedule-require-validation"
                   onChange={(e) => setSched({ ...sched, require_validation: e.target.checked })} />
            Les réservations clients doivent être validées par le garage
          </label>
        </div>
        <div className="mt-7"><RedButton onClick={saveSchedule} data-testid="schedule-save">Enregistrer les horaires</RedButton></div>
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0a] p-5 md:p-7">
        <div className="overline mb-5">Congés & fermetures</div>
        <div className="grid gap-4 sm:grid-cols-[1fr,1fr,1fr,auto] items-end">
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Du</span>
            <input type="date" value={newClosure.start_date} data-testid="closure-start"
                   onChange={(e) => setNewClosure({ ...newClosure, start_date: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Au</span>
            <input type="date" value={newClosure.end_date} data-testid="closure-end"
                   onChange={(e) => setNewClosure({ ...newClosure, end_date: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Motif</span>
            <input value={newClosure.reason} data-testid="closure-reason" placeholder="Congés"
                   onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <RedButton className="px-4 py-2.5 text-sm" data-testid="closure-add"
                     onClick={async () => {
                       if (!newClosure.start_date) { toast.error("Indiquez une date de début"); return; }
                       try { await api.post("/admin/closures", newClosure); toast.success("Fermeture enregistrée"); setNewClosure({ start_date: "", end_date: "", reason: "" }); load(); }
                       catch (e) { toast.error(apiError(e)); }
                     }}>Ajouter</RedButton>
        </div>
        <div className="mt-6 grid gap-2">
          {closures.map((c) => (
            <div key={`${c.start_date}-${c.end_date}`} className="flex items-center justify-between border border-zinc-900 px-4 py-2.5"
                 data-testid={`closure-${c.start_date}`}>
              <span className="text-sm text-white">{c.start_date} → {c.end_date} · <span className="text-zinc-400">{c.reason}</span></span>
              <button className="text-zinc-500 hover:text-sared" data-testid={`closure-delete-${c.start_date}`}
                      onClick={async () => { await api.delete(`/admin/closures?start_date=${c.start_date}&end_date=${c.end_date}`); toast.success("Supprimé"); load(); }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {closures.length === 0 && <p className="text-sm text-zinc-600">Aucune fermeture programmée</p>}
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0a] p-5 md:p-7">
        <div className="overline mb-5">Horaires exceptionnels (un jour précis)</div>
        <div className="grid gap-4 sm:grid-cols-[1fr,auto,1fr,1fr,auto] items-end">
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Date</span>
            <input type="date" value={newOverride.date} data-testid="override-date"
                   onChange={(e) => setNewOverride({ ...newOverride, date: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 pb-2.5">
            <input type="checkbox" checked={newOverride.open} data-testid="override-open"
                   onChange={(e) => setNewOverride({ ...newOverride, open: e.target.checked })} />
            Ouvert
          </label>
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Début</span>
            <input type="time" value={newOverride.start} data-testid="override-start"
                   onChange={(e) => setNewOverride({ ...newOverride, start: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Fin</span>
            <input type="time" value={newOverride.end} data-testid="override-end"
                   onChange={(e) => setNewOverride({ ...newOverride, end: e.target.value })}
                   className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 text-white focus:border-sared focus:outline-none" />
          </label>
          <RedButton className="px-4 py-2.5 text-sm" data-testid="override-add"
                     onClick={async () => {
                       if (!newOverride.date) { toast.error("Indiquez une date"); return; }
                       try { await api.post("/admin/overrides", newOverride); toast.success("Horaire exceptionnel enregistré"); load(); }
                       catch (e) { toast.error(apiError(e)); }
                     }}>Ajouter</RedButton>
        </div>
        <div className="mt-6 grid gap-2">
          {overrides.map((o) => (
            <div key={o.date} className="flex items-center justify-between border border-zinc-900 px-4 py-2.5" data-testid={`override-${o.date}`}>
              <span className="text-sm text-white">{o.date} · {o.open ? `${o.start} → ${o.end}` : "Fermé"} {o.reason ? <span className="text-zinc-400">· {o.reason}</span> : null}</span>
              <button className="text-zinc-500 hover:text-sared" data-testid={`override-delete-${o.date}`}
                      onClick={async () => { await api.delete(`/admin/overrides?date=${o.date}`); toast.success("Supprimé"); load(); }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {overrides.length === 0 && <p className="text-sm text-zinc-600">Aucun horaire exceptionnel</p>}
        </div>
      </div>
    </div>
  );
}
